import React, { useContext } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import RecentlyViewed from '../components/RecentlyViewed';

const AccountLayout = () => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();

  // Derive initials from full name
  const initials = user?.fullName
    ? user.fullName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2)
    : 'U';

  const navLinks = [
    { name: 'Overview', path: '/account/dashboard', icon: 'dashboard' },
    { name: 'My Expert Advisors', path: '/account/my-eas', icon: 'monitoring' },
    { name: 'My Orders', path: '/account/orders', icon: 'receipt_long' },
    { name: 'My Wishlist', path: '/account/wishlist', icon: 'favorite' },
    { name: "Can't Find an EA?", path: '/account/requests', icon: 'search' },
    { name: 'Custom Algo Requests', path: '/account/custom-algo-requests', icon: 'code' },
    { name: 'Settings', path: '/account/settings', icon: 'settings' },
  ];

  return (
    <div className="min-h-screen bg-bg py-10 relative">
      {/* Background glow */}
      <div className="absolute top-0 right-0 w-[800px] h-[500px] bg-primary/5 rounded-[100%] blur-[120px] -z-10 pointer-events-none"></div>

      <div className="max-w-[1400px] mx-auto px-margin-mobile md:px-margin-desktop grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* LEFT SIDEBAR */}
        <aside className="lg:col-span-3 space-y-6">
          
          {/* Profile Card */}
          <div className="bg-surface-container border border-white/5 rounded-3xl p-6 flex flex-col items-center text-center shadow-lg relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary/50 to-transparent"></div>
            
            <div className="w-20 h-20 rounded-full bg-surface-container-highest border border-white/10 flex items-center justify-center text-[24px] font-bold text-primary mb-4 shadow-[0_0_20px_rgba(240,185,11,0.15)]">
              {initials}
            </div>
            
            <h3 className="text-white font-bold text-[18px] mb-1">{user?.fullName || 'User Name'}</h3>
            <p className="text-on-surface-variant text-[13px] mb-4">{user?.email || 'email@example.com'}</p>
            
            <span className="bg-[#4a4014]/40 border border-primary/20 text-primary text-[10px] font-bold px-3 py-1 rounded-md tracking-widest uppercase">
              Trader
            </span>
          </div>

          {/* Navigation Menu */}
          <nav className="bg-surface-container border border-white/5 rounded-3xl overflow-hidden shadow-lg">
            <ul className="flex flex-col text-[14px]">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <li key={link.path}>
                    <Link 
                      to={link.path} 
                      className={`flex items-center gap-4 px-6 py-4 font-medium transition-colors ${
                        isActive 
                          ? 'bg-primary text-[#131418] shadow-[0_0_15px_rgba(240,185,11,0.2)]' 
                          : 'text-silver-text hover:bg-white/5 border-b border-white/5'
                      }`}
                    >
                      <span className={`material-symbols-outlined text-[18px] ${
                        isActive 
                          ? 'text-[#131418]' 
                          : (link.icon === 'code' ? 'text-[#4285F4]' : link.icon === 'settings' ? 'text-[#A142F4]' : link.icon === 'search' ? 'text-[#20b2aa]' : 'text-on-surface-variant')
                      }`}>
                        {link.icon}
                      </span>
                      {link.name}
                    </Link>
                  </li>
                );
              })}
              <li>
                <button onClick={logout} className="w-full flex items-center gap-4 px-6 py-4 text-red-400 hover:bg-white/5 transition-colors font-medium text-left">
                  <span className="material-symbols-outlined text-[18px]">logout</span>
                  Logout
                </button>
              </li>
            </ul>
          </nav>

        </aside>

        {/* RIGHT MAIN CONTENT (Outlet renders nested routes) */}
        <main className="lg:col-span-9 space-y-8 relative z-10">
          <Outlet />
        </main>
      </div>

      <div className="mt-16">
        <RecentlyViewed />
      </div>

    </div>
  );
};

export default AccountLayout;
